import React, { useState } from 'react'
import AsyncSelect from 'react-select/async'
import { useConfig } from '@peracto/peracto-config'
import TextInput from '@peracto/peracto-editor-ui/dist/TextInput'
import Select from '@peracto/peracto-editor-ui/dist/Select'
import Slider from '@peracto/peracto-editor-ui/dist/Slider'
import File from '@peracto/peracto-editor-ui/dist/File'
import styled from 'styled-components'
import debounce from 'debounce-promise'
import axios from 'axios'
import startCase from 'lodash/startCase'
import uniqBy from 'lodash/uniqBy'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { faSave } from '@fortawesome/pro-regular-svg-icons/faSave'

const DEFAULT_ATTRIBUTE = { attribute: '', value: '', label: 'Please select...' }

const AttributeDivider = styled.div`
    height: 30px;
    position: relative;
    text-align: center;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    justify-content: center;

    &:before {
        display: inline-block;
        content: ${props => (props.attributeOperator ? `'${props.attributeOperator}'` : '')};
        background-color: ${props => props.theme.admin.colors.brandDark};
        padding-left: 10px;
        padding-right: 10px;
        z-index: 1;
    }

    &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        background-color: ${props => props.theme.admin.colors.greyLight};
    }
`

const ProductsCarouselForm = ({ onChange, state }) => {
    const {
        numberOfProducts = 4,
        attributes = [{ ...DEFAULT_ATTRIBUTE }],
        attributeOperator = 'and',
        cardStyle = 'square',
        progressIndicatorStyle = 'dots',
        imageFit = 'cover',
        showPrices = false,
        incVAT = false,
        useSiloImages = false,
        fontFamily = '',
        fontWeight = 'normal',
        fontColour = '#000',
        accentColour = '#000',
        arrowColour = '#FFFFFF',
        fallbackImage = '',
    } = state

    const [localAttributes, setLocalAttributes] = useState(attributes)

    const config = useConfig()
    const API_URL = config.get('api')
    const { theme, fonts, colorOptions } = config.get('editorConfig') || []

    const themeColours = []
    const themeFonts = []
    const themeFontWeights = [
        { label: 'Light', value: 'light' },
        { label: 'Normal', value: 'normal' },
        { label: 'Bold', value: 'bold' },
    ]

    if (colorOptions) {
        // Tailwind
        themeColours.push(...colorOptions)
    } else if (theme?.colors?.brand) {
        // Editor theme
        for (const [label, colour] of Object.entries(theme.colors.brand)) {
            themeColours.push({
                label: startCase(label),
                value: colour,
            })
        }
    }

    if (fonts) {
        // Tailwind
        for (const [label, font] of Object.entries(fonts)) {
            themeFonts.push({
                label: startCase(label),
                value: Array.isArray(font) ? font[0] : font,
            })
        }
    } else if (theme?.fonts) {
        // Editor theme
        for (const [label, font] of Object.entries(theme.fonts)) {
            themeFonts.push({
                label: startCase(label),
                value: font,
            })
        }
    }

    const fetchAttributes = async input => {
        const { data } = await axios.get(
            `${API_URL}/attributes?label=${input || ''}&filterable=true`,
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            }
        )

        const parsedData = data['hydra:member']

        const values = parsedData.map(form => ({
            label: form.label,
            value: form.code,
        }))

        return [{ label: 'SKU', value: 'sku' }, ...values]
    }

    const debouncedFetchAttributes = debounce(fetchAttributes, 200)

    return (
        <>
            <div className="!mb-0 form-group">
                <h6>Attributes</h6>
                {localAttributes.map((attr, idx) => (
                    <React.Fragment key={`attributes_${attr.label}_${idx}`}>
                        {idx > 0 && <AttributeDivider attributeOperator={attributeOperator} />}

                        <label className="!mb-1">Attribute Name</label>

                        <AsyncSelect
                            className="!mb-2 !w-full"
                            loadOptions={input => debouncedFetchAttributes(input)}
                            isSearchable={true}
                            defaultOptions={true}
                            value={{
                                label: attr.label,
                                value: attr.attribute,
                            }}
                            onChange={option => {
                                let attrs = [...localAttributes]
                                attrs[idx].attribute = option.value
                                attrs[idx].label = option.label

                                setLocalAttributes([...attrs])
                            }}
                            placeholder="Select an attribute..."
                            classNamePrefix="peracto-select"
                            noOptionsMessage={({ inputValue }) => {
                                if (inputValue.length > 0) {
                                    return `No attributes found for '${inputValue}'.`
                                } else {
                                    return 'Enter text to begin searching.'
                                }
                            }}
                        />

                        <TextInput
                            label="Attribute Value"
                            onChange={e => {
                                const attrs = [...localAttributes]
                                attrs[idx].value = e.target.value

                                setLocalAttributes([...attrs])
                            }}
                            value={localAttributes[idx].value}
                        />

                        {idx > 0 && (
                            <button
                                className="!my-2 btn btn-sm btn-danger !w-full"
                                onClick={e => {
                                    const attrs = [...localAttributes]
                                    attrs.splice(idx, 1)

                                    setLocalAttributes([...attrs])
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} className="!mr-2" /> Remove
                                Attribute
                            </button>
                        )}
                    </React.Fragment>
                ))}
            </div>

            <AttributeDivider />

            <button
                className="!my-2 btn btn-sm btn-secondary !w-full"
                onClick={e => {
                    const attrs = [...localAttributes, { ...DEFAULT_ATTRIBUTE }]

                    setLocalAttributes(attrs)
                }}
            >
                <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" /> Add Attribute
            </button>

            <hr />

            <button
                className="!my-2 btn btn-sm btn-success !w-full"
                onClick={e => {
                    onChange({
                        attributes: localAttributes,
                    })
                }}
            >
                <FontAwesomeIcon icon={faSave} className="!mr-2" /> Save Attributes
            </button>

            <Select
                label="Attribute Operator"
                options={[
                    { label: 'And', value: 'and' },
                    { label: 'Or', value: 'or' },
                ]}
                value={attributeOperator}
                onChange={e => onChange({ attributeOperator: e.value })}
            />

            <Slider
                label={`Number of Products: ${numberOfProducts}`}
                min={1}
                max={20}
                step={1}
                value={numberOfProducts}
                onChange={e => onChange({ numberOfProducts: e.target.value })}
            />

            <Select
                label="Card Style"
                options={[
                    { label: 'Square', value: 'square' },
                    { label: 'Rectangle', value: 'rectangle' },
                ]}
                value={cardStyle}
                onChange={e => onChange({ cardStyle: e.value })}
            />

            <Select
                label="Progress Indicator Style"
                options={[
                    { label: 'Dots', value: 'dots' },
                    // { label: 'Bar', value: 'bar' },
                ]}
                defaultValue={{ label: 'Dots', value: 'dots' }}
                value={progressIndicatorStyle}
                onChange={e => onChange({ progressIndicatorStyle: e.value })}
            />

            <Select
                label="Image Fit"
                options={[
                    { label: 'Cover', value: 'cover' },
                    { label: 'Contain', value: 'contain' },
                ]}
                value={imageFit}
                onChange={e => onChange({ imageFit: e.value })}
            />

            <Select
                label="Show Prices"
                options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ]}
                value={showPrices}
                onChange={e => onChange({ showPrices: e.value })}
            />

            <Select
                label="Include VAT"
                options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                ]}
                value={incVAT}
                onChange={e => onChange({ incVAT: e.value })}
            />

            <Select
                label="Silo Images"
                options={[
                    { label: 'Yes (Needs Silo Image Resource)', value: true },
                    { label: 'No', value: false },
                ]}
                value={useSiloImages}
                onChange={e => onChange({ useSiloImages: e.value })}
            />

            <File
                label="Fallback Image"
                onChange={value => onChange({ fallbackImage: value })}
                value={fallbackImage}
                isInline={false}
            />

            <Select
                label="Font Family"
                options={uniqBy(themeFonts, fontFamily => fontFamily.value)}
                value={fontFamily}
                onChange={e => onChange({ fontFamily: e.value })}
            />

            <Select
                label="Font Weight"
                options={themeFontWeights}
                value={fontWeight}
                onChange={e => onChange({ fontWeight: e.value })}
            />

            <Select
                label="Font Colour"
                options={themeColours}
                value={fontColour}
                onChange={e => onChange({ fontColour: e.value })}
            />

            <Select
                label="Accent Colour"
                options={themeColours}
                value={accentColour}
                onChange={e => onChange({ accentColour: e.value })}
            />

            <Select
                label="Arrow Colour"
                options={themeColours}
                value={arrowColour}
                onChange={e => onChange({ arrowColour: e.value })}
            />
        </>
    )
}

export default ProductsCarouselForm
